import { useEffect, useMemo, useState } from "react";

import type {
  DashboardRealtimeSnapshot,
  DashboardRealtimeSubscribe,
} from "../realtime/dashboardRealtimeTypes";

import { buildWebSocketUrl } from "../realtime/buildWebSocketUrl";
import { DashboardRealtimeClient } from "../realtime/dashboardRealtimeClient";
import { getSingletonDashboardRealtimeClient } from "../realtime/singletonDashboardRealtimeClient";

export function useDashboardRealtime({
  enabled = true,
}: { enabled?: boolean } = {}): DashboardRealtimeSnapshot & {
  subscribe: DashboardRealtimeSubscribe;
} {
  const [client, setClient] = useState(() =>
    getSingletonDashboardRealtimeClient(),
  );
  const [snapshot, setSnapshot] = useState<DashboardRealtimeSnapshot>(() =>
    client.getSnapshot(),
  );

  useEffect(() => client.subscribeSnapshot(setSnapshot), [client]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const current = getSingletonDashboardRealtimeClient();
    if (current !== client) {
      // The captured client destroyed itself (refcount hit zero — e.g. the
      // StrictMode mount/unmount/remount replay) and a new singleton was
      // created. Resync state so every consumer of this hook re-binds to the
      // live client instead of silently holding the dead one.
      setClient(current);
      return;
    }
    return current.connectRef();
  }, [client, enabled]);

  const subscribe = useMemo<DashboardRealtimeSubscribe>(
    () => (handler, options) => client.subscribeMessage(handler, options),
    [client],
  );

  return { ...snapshot, subscribe };
}

export const __testing = { DashboardRealtimeClient, buildWebSocketUrl };
